/**
 * Admin API client — talks to admin-api (tacosai-admin-api-beta.fly.dev).
 *
 * Auth: pulls the current access_token from token-store on every request.
 * On a 401, attempts ONE refresh + retry; on second 401, clears tokens and
 * re-throws so the AuthGuard kicks the user back into the PKCE flow.
 *
 * The access_token is requested fresh on each call (cheap — sessionStorage
 * read + Date.now() compare); if needsRefresh() is true the call awaits a
 * refresh first, so a single in-flight request can serialize concurrent
 * usage well enough for an admin tool.
 */

import { getTokens, hasValidAccessToken, needsRefresh, clearTokens } from '../../auth/token-store.js';
import { refresh } from '../../auth/auth-client.js';

const API_BASE = import.meta.env['VITE_ADMIN_API_BASE'] ?? 'https://tacosai-admin-api-beta.fly.dev';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public details?: unknown,
  ) {
    super(`${status} ${code}`);
  }
}

interface RequestOpts {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  url: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

async function getBearer(): Promise<string | null> {
  const t = getTokens();
  if (!t) return null;
  if (hasValidAccessToken()) return t.access_token;
  if (needsRefresh()) {
    try {
      return await refresh();
    } catch {
      clearTokens();
      return null;
    }
  }
  return t.access_token;
}

async function doFetch(opts: RequestOpts, bearer: string | null): Promise<Response> {
  const params = opts.query
    ? '?' +
      new URLSearchParams(
        Object.entries(opts.query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)] as [string, string]),
      )
    : '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
  return fetch(`${API_BASE}${opts.url}${params}`, {
    method: opts.method,
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
}

export async function request<T>(opts: RequestOpts): Promise<T> {
  let bearer = await getBearer();
  let res = await doFetch(opts, bearer);

  // On 401, try ONE refresh + retry. Avoids the obvious infinite-loop trap.
  if (res.status === 401 && bearer) {
    try {
      bearer = await refresh();
      res = await doFetch(opts, bearer);
    } catch {
      clearTokens();
      throw new ApiError(401, 'unauthenticated_refresh_failed');
    }
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const errBodyObj = errBody as { error?: unknown; details?: unknown };
    throw new ApiError(
      res.status,
      typeof errBodyObj.error === 'string' ? errBodyObj.error : 'unknown_error',
      errBodyObj.details,
    );
  }
  // 204 / empty body case.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
